/**
 * 宠物学习的读写口（adm1 TEACH-ORDER-QUOTA-117 的 13 条玩家接口里页面要用的）。
 * 学会的动作的片段不在这里取：走 pets 服务现成的 motion 读口（A-2 TAUGHT-CATALOG-117 把教学段并进去），见 ./teachClips。
 * 放在模块自己这里、不往 shared/services 加键：6c2b 的 117-3 正在改 shared/services/types.ts（10-16 对过写集）。
 * 写口里要幂等键的（建草稿、确认、取消、启用、停用、删除），由页面按「一次用户动作」生成一个，重试沿用同一个；不在这里自动换键。
 * 免费微调（adm1 POST …/adjust）这一版不接：它不收「要调什么」，按下去就用掉唯一一次（设计评审第一轮高 3），等后端收调整内容再加。
 * 整理、回答、参考照片（TEACH-UNDERSTAND-UI-117，照 adm1 TEACH-WIRE-117 HTTP-CONTRACT.md）：
 * - 整理（understand）、回答（answers）带页面上那一版的 draft_revision，**不带幂等键**：同一版同一轮后端只整理一次，再点给原结果；
 * - 传参考照片要幂等键（同一张照片重发沿用同一个键），kind 只用 photo（视频帧进 118）；删参考按媒体号删。
 */
import type { ApiClient } from "@/shared/api/client";
import type { TeachEntitlement, TeachOrder, TeachReferenceResult, TeachSpec, TeachUnderstandResult } from "./teachTypes";

export interface TeachOrderList {
  items: TeachOrder[];
  entitlement: TeachEntitlement;
}
export interface TeachOrderWithEntitlement {
  order: TeachOrder;
  entitlement: TeachEntitlement;
}
export interface TeachCancelResult extends TeachOrderWithEntitlement {
  outcome: string;
}

export interface TeachApi {
  entitlement(signal?: AbortSignal): Promise<TeachEntitlement>;
  ordersOfPet(petId: string, signal?: AbortSignal): Promise<TeachOrderList>;
  order(orderId: string, signal?: AbortSignal): Promise<TeachOrder>;
  createDraft(petId: string, draft: { draft_text: string; refs: string[] }, idempotencyKey: string): Promise<TeachOrder>;
  editDraft(orderId: string, draft: { draft_text: string; refs: string[] }): Promise<TeachOrder>;
  editSpec(orderId: string, spec: TeachSpec): Promise<TeachOrder>;
  confirm(orderId: string, specSha256: string, idempotencyKey: string): Promise<TeachOrderWithEntitlement>;
  cancel(orderId: string, idempotencyKey: string): Promise<TeachCancelResult>;
  setEnabled(orderId: string, enabled: boolean, idempotencyKey: string): Promise<TeachOrder>;
  remove(orderId: string, idempotencyKey: string): Promise<TeachOrderWithEntitlement>;
  understand(orderId: string, draftRevision: number): Promise<TeachUnderstandResult>;
  answers(orderId: string, draftRevision: number, answers: string[]): Promise<TeachUnderstandResult>;
  addPhoto(orderId: string, file: Blob, idempotencyKey: string): Promise<TeachReferenceResult>;
  removeReference(orderId: string, mediaId: string): Promise<TeachOrder>;
}

const seg = encodeURIComponent;

export function liveTeachApi(api: ApiClient): TeachApi {
  return {
    entitlement: (signal) => api.request<TeachEntitlement>("/teach/entitlement", { signal }),
    ordersOfPet: (petId, signal) => api.request<TeachOrderList>(`/pets/${seg(petId)}/teach/orders`, { signal }),
    order: (orderId, signal) => api.request<TeachOrder>(`/teach/orders/${seg(orderId)}`, { signal }),
    createDraft: (petId, draft, idempotencyKey) => api.request<TeachOrder>(`/pets/${seg(petId)}/teach/orders`, { method: "POST", body: draft, idempotencyKey }),
    editDraft: (orderId, draft) => api.request<TeachOrder>(`/teach/orders/${seg(orderId)}`, { method: "PUT", body: draft }),
    editSpec: (orderId, spec) => api.request<TeachOrder>(`/teach/orders/${seg(orderId)}/spec`, { method: "PUT", body: spec }),
    confirm: (orderId, specSha256, idempotencyKey) => api.request<TeachOrderWithEntitlement>(`/teach/orders/${seg(orderId)}/confirm`, { method: "POST", body: { spec_sha256: specSha256 }, idempotencyKey }),
    cancel: (orderId, idempotencyKey) => api.request<TeachCancelResult>(`/teach/orders/${seg(orderId)}/cancel`, { method: "POST", idempotencyKey }),
    setEnabled: (orderId, enabled, idempotencyKey) => api.request<TeachOrder>(`/teach/orders/${seg(orderId)}/${enabled ? "enable" : "disable"}`, { method: "POST", idempotencyKey }),
    remove: (orderId, idempotencyKey) => api.request<TeachOrderWithEntitlement>(`/teach/orders/${seg(orderId)}`, { method: "DELETE", idempotencyKey }),
    understand: (orderId, draftRevision) => api.request<TeachUnderstandResult>(`/teach/orders/${seg(orderId)}/understand`, { method: "POST", body: { draft_revision: draftRevision } }),
    answers: (orderId, draftRevision, answers) => api.request<TeachUnderstandResult>(`/teach/orders/${seg(orderId)}/answers`, { method: "POST", body: { draft_revision: draftRevision, answers } }),
    addPhoto: (orderId, file, idempotencyKey) => {
      const form = new FormData();
      form.append("file", file);
      form.append("kind", "photo");
      return api.request<TeachReferenceResult>(`/teach/orders/${seg(orderId)}/references`, { method: "POST", body: form, idempotencyKey });
    },
    removeReference: (orderId, mediaId) => api.request<TeachOrder>(`/teach/orders/${seg(orderId)}/references/${seg(mediaId)}`, { method: "DELETE" }),
  };
}
